import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getAllKycDocuments,
  approveKyc,
  rejectKyc,
  setCurrentPage,
} from "../../store/slices/adminSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Drawer from "../../components/UI/Drawer";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";

import {
  DocumentTextIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const AdminKyc: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    allKycDocuments, // Use allKycDocuments instead of users
    isLoading,
    error,
    totalPages,
    currentPage,
    totalItems,
    actionLoading,
  } = useSelector((state: RootState) => state.admin);

  const [selectedKyc, setSelectedKyc] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showActionModal, setShowActionModal] = useState<{
    open: boolean;
    type: "approve" | "reject";
    kyc: any;
  }>({ open: false, type: "approve", kyc: null });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [imageModal, setImageModal] = useState<{
    open: boolean;
    type: "front" | "back" | "selfie";
    src: string;
    title: string;
  }>({ open: false, type: "front", src: "", title: "" });

  // Use allKycDocuments directly
  const kycDocuments = allKycDocuments || [];

  useEffect(() => {
    loadKycDocuments();
    // eslint-disable-next-line
  }, [currentPage, statusFilter]);

  const loadKycDocuments = () => {
    const filters: any = {
      page: currentPage,
      limit: 20,
    };

    if (statusFilter !== "all") {
      filters.status = statusFilter;
    }

    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    dispatch(getAllKycDocuments(filters));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Reset to first page when searching
    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
    loadKycDocuments();
  };

  const handleApproveKyc = async () => {
    if (!selectedKyc && !showActionModal.kyc) return;

    const kycId = selectedKyc?.id || showActionModal.kyc?.id;
    const result = await dispatch(
      approveKyc({
        kycId,
        notes: approvalNotes.trim() || undefined,
      }),
    );

    if (approveKyc.fulfilled.match(result)) {
      setShowActionModal({ open: false, type: "approve", kyc: null });
      setApprovalNotes("");
      setSelectedKyc(null);
      setShowDetails(false);
      loadKycDocuments();
    }
  };

  const handleRejectKyc = async () => {
    if (!selectedKyc && !showActionModal.kyc) return;

    if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
      alert("Please provide a rejection reason (min 10 characters)");
      return;
    }

    const kycId = selectedKyc?.id || showActionModal.kyc?.id;
    const result = await dispatch(
      rejectKyc({
        kycId,
        rejectionReason: rejectionReason.trim(),
      }),
    );

    if (rejectKyc.fulfilled.match(result)) {
      setShowActionModal({ open: false, type: "reject", kyc: null });
      setRejectionReason("");
      setSelectedKyc(null);
      setShowDetails(false);
      loadKycDocuments();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getDocumentTypeName = (type: string) => {
    switch (type) {
      case "id_card":
        return "ID Card";
      case "passport":
        return "Passport";
      case "driver_license":
        return "Driver's License";
      default:
        return (
          type
            ?.split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ") || "Unknown"
        );
    }
  };

  const getImageUrl = (path: string) => {
    if (!path) return "";

    const cleanPath = path.replace(/^\//, "");

    // Use environment variable or fallback
    const storageUrl =
      process.env.REACT_APP_STORAGE_URL ||
      process.env.REACT_APP_API_URL?.replace("/api", "/storage") ||
      "https://backend.regalflux.com/public/storage";

    return `${storageUrl}/${cleanPath}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeSuccess}`}>
            Approved
          </span>
        );
      case "pending":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeWarning}`}>
            Pending
          </span>
        );
      case "rejected":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeDanger}`}>
            Rejected
          </span>
        );
      default:
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeInfo}`}>
            {status || "Unknown"}
          </span>
        );
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Stats calculation - ensure we're using the actual data
  const pendingCount = kycDocuments.filter(
    (k: any) => k.status === "pending",
  ).length;
  const approvedCount = kycDocuments.filter(
    (k: any) => k.status === "approved",
  ).length;
  const rejectedCount = kycDocuments.filter(
    (k: any) => k.status === "rejected",
  ).length;

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className={globalStyle.h3}>KYC Management</h1>
            <p className={globalStyle.body2 + " text-gray-400"}>
              Review and manage user identity documents
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button
              variant="secondary"
              onClick={() => {
                loadKycDocuments();
              }}
              loading={isLoading}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2 text-primary" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={globalStyle.grid4}>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Total KYC
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {totalItems || 0}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-900 flex items-center justify-center">
                <DocumentTextIcon className="w-6 h-6 text-blue-300" />
              </div>
            </div>
          </Card>

          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Pending Review
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {pendingCount}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-900 flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-yellow-300" />
              </div>
            </div>
          </Card>

          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Approved
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {approvedCount}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-900 flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-green-300" />
              </div>
            </div>
          </Card>

          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Rejected
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {rejectedCount}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-900 flex items-center justify-center">
                <XCircleIcon className="w-6 h-6 text-red-300" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mt-6" padding="lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by username, email, name, or document type..."
                  className={`${globalStyle.input} pl-10`}
                />
              </div>
            </form>

            <div className="flex items-center space-x-3">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as any);
                    dispatch(setCurrentPage(1)); // Reset to first page on filter change
                  }}
                  className={`${globalStyle.input} pr-10`}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <FunnelIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
              <Button onClick={handleSearch} loading={isLoading}>
                Search
              </Button>
            </div>
          </div>
        </Card>

        {/* KYC Applications Table */}
        <Card className="mt-6" padding="lg">
          {error && <Alert type="error" message={error} className="mb-6" />}

          {isLoading ? (
            <div className="text-center py-12">
              <svg
                className={`${globalStyle.loadingSpinner} mx-auto`}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className={`${globalStyle.body2} mt-4 text-gray-400`}>
                Loading KYC documents...
              </p>
            </div>
          ) : kycDocuments.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className={globalStyle.h5}>No KYC Documents Found</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                {searchTerm || statusFilter !== "all"
                  ? "Try different search terms or filters"
                  : "No KYC documents submitted yet"}
              </p>
              {(searchTerm || statusFilter !== "all") && (
                <Button
                  variant="tertiary"
                  className="mt-4"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    dispatch(setCurrentPage(1));
                    loadKycDocuments();
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className={globalStyle.table}>
                  <thead className={globalStyle.tableHead}>
                    <tr>
                      <th className={globalStyle.tableHeadCell}>User</th>
                      <th className={globalStyle.tableHeadCell}>
                        Document Type
                      </th>
                      <th className={globalStyle.tableHeadCell}>Status</th>
                      <th className={globalStyle.tableHeadCell}>Submitted</th>
                      <th className={globalStyle.tableHeadCell}>Reviewed</th>
                      <th className={globalStyle.tableHeadCell}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={globalStyle.tableBody}>
                    {kycDocuments.map((kyc: any) => (
                      <tr key={kyc.id} className={globalStyle.tableRowHover}>
                        <td className={globalStyle.tableCell}>
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3">
                              <UserIcon className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {kyc.user?.username || "N/A"}
                              </p>
                              <p
                                className={
                                  globalStyle.caption + " text-gray-400"
                                }
                              >
                                {kyc.user?.email || "N/A"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className={globalStyle.tableCell}>
                          <span className="capitalize text-gray-300">
                            {getDocumentTypeName(kyc.document_type)}
                          </span>
                        </td>
                        <td className={globalStyle.tableCell}>
                          {getStatusBadge(kyc.status)}
                        </td>
                        <td className={globalStyle.tableCell}>
                          <span className="text-gray-300">
                            {formatDate(kyc.created_at)}
                          </span>
                        </td>
                        <td className={globalStyle.tableCell}>
                          <span className="text-gray-300">
                            {kyc.reviewed_at
                              ? formatDate(kyc.reviewed_at)
                              : "Not reviewed"}
                          </span>
                        </td>
                        <td className={globalStyle.tableCell}>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="tertiary"
                              onClick={() => {
                                setSelectedKyc(kyc);
                                setShowDetails(true);
                              }}
                              title="View Details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </Button>
                            {kyc.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => {
                                    setShowActionModal({
                                      open: true,
                                      type: "approve",
                                      kyc,
                                    });
                                    setApprovalNotes("");
                                  }}
                                  title="Approve KYC"
                                >
                                  <CheckCircleIcon className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => {
                                    setShowActionModal({
                                      open: true,
                                      type: "reject",
                                      kyc,
                                    });
                                    setRejectionReason("");
                                  }}
                                  title="Reject KYC"
                                >
                                  <XCircleIcon className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-800">
                  <div>
                    <p className={globalStyle.caption + " text-gray-400"}>
                      Showing {(currentPage - 1) * 20 + 1} to{" "}
                      {Math.min(currentPage * 20, totalItems)} of {totalItems}{" "}
                      KYC documents
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={
                                currentPage === pageNum ? "primary" : "tertiary"
                              }
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              className="w-10"
                            >
                              {pageNum}
                            </Button>
                          );
                        },
                      )}
                    </div>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRightIcon className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </main>

      <BottomNav />

      {/* KYC Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title="KYC Document Details"
        size="xl"
      >
        {selectedKyc && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center mr-3">
                  <UserIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className={globalStyle.h5 + " text-white"}>
                    {selectedKyc.user?.username || "N/A"}
                  </h3>
                  <p className={globalStyle.caption + " text-gray-400"}>
                    {selectedKyc.user?.email || "N/A"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="mb-2">{getStatusBadge(selectedKyc.status)}</div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Submitted
                </p>
                <p className="text-white">
                  {formatDate(selectedKyc.created_at)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400 mb-3`}>
                  Document Information
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">Document Type</p>
                    <p className="font-medium capitalize text-white">
                      {getDocumentTypeName(selectedKyc.document_type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">
                      {getStatusBadge(selectedKyc.status)}
                    </div>
                  </div>
                  {selectedKyc.reviewed_at && (
                    <div>
                      <p className="text-sm text-gray-500">Reviewed At</p>
                      <p className="text-white">
                        {formatDate(selectedKyc.reviewed_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400 mb-3`}>
                  User Information
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium text-white">
                      {selectedKyc.user?.first_name || "N/A"}{" "}
                      {selectedKyc.user?.last_name || ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Registration Date</p>
                    <p className="text-white">
                      {formatDate(selectedKyc.user?.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Account Status</p>
                    <span
                      className={`${globalStyle.badge} ${
                        selectedKyc.user?.is_active
                          ? globalStyle.badgeSuccess
                          : globalStyle.badgeDanger
                      }`}
                    >
                      {selectedKyc.user?.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400 mb-3`}>
                  Verification Details
                </h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">Email Verified</p>
                    <p className="font-medium text-white">
                      {selectedKyc.user?.email_verified_at ? "Yes" : "No"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">User KYC Status</p>
                    <p className="capitalize text-white">
                      {selectedKyc.user?.kyc_status || "N/A"}
                    </p>
                  </div>
                  {selectedKyc.rejection_reason && (
                    <div>
                      <p className="text-sm text-gray-500">Rejection Reason</p>
                      <p className="text-sm text-red-400">
                        {selectedKyc.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Document Images */}
            <div>
              <h4 className={`${globalStyle.h6} text-gray-400 mb-4`}>
                Document Images
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Front Image */}
                <div className="text-center">
                  <p className="text-sm font-medium mb-2 text-white">
                    Front of Document
                  </p>
                  <div
                    className="border-2 border-gray-700 rounded-lg overflow-hidden cursor-pointer hover:border-primary transition-colors bg-gray-800"
                    onClick={() =>
                      setImageModal({
                        open: true,
                        type: "front",
                        src: getImageUrl(selectedKyc.front_image_path),
                        title: "Front of Document",
                      })
                    }
                  >
                    <div className="aspect-video flex items-center justify-center relative">
                      {selectedKyc.front_image_path ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <DocumentMagnifyingGlassIcon className="w-12 h-12 text-gray-500" />
                        </div>
                      ) : (
                        <ExclamationTriangleIcon className="w-12 h-12 text-gray-500" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Click to view full size
                  </p>
                </div>

                {/* Back Image (if exists) */}
                <div className="text-center">
                  <p className="text-sm font-medium mb-2 text-white">
                    Back of Document
                  </p>
                  <div
                    className={`border-2 rounded-lg overflow-hidden ${
                      selectedKyc.back_image_path
                        ? "border-gray-700 cursor-pointer hover:border-primary transition-colors bg-gray-800"
                        : "border-gray-800 bg-gray-900"
                    }`}
                    onClick={() => {
                      if (selectedKyc.back_image_path) {
                        setImageModal({
                          open: true,
                          type: "back",
                          src: getImageUrl(selectedKyc.back_image_path),
                          title: "Back of Document",
                        });
                      }
                    }}
                  >
                    <div className="aspect-video flex items-center justify-center relative">
                      {selectedKyc.back_image_path ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <DocumentMagnifyingGlassIcon className="w-12 h-12 text-gray-500" />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Not Provided</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedKyc.back_image_path
                      ? "Click to view full size"
                      : "Optional field"}
                  </p>
                </div>

                {/* Selfie */}
                <div className="text-center">
                  <p className="text-sm font-medium mb-2 text-white">
                    Selfie with Document
                  </p>
                  <div
                    className="border-2 border-gray-700 rounded-lg overflow-hidden cursor-pointer hover:border-primary transition-colors bg-gray-800"
                    onClick={() =>
                      setImageModal({
                        open: true,
                        type: "selfie",
                        src: getImageUrl(selectedKyc.selfie_path),
                        title: "Selfie with Document",
                      })
                    }
                  >
                    <div className="aspect-video flex items-center justify-center relative">
                      {selectedKyc.selfie_path ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <UserIcon className="w-12 h-12 text-gray-500" />
                        </div>
                      ) : (
                        <ExclamationTriangleIcon className="w-12 h-12 text-gray-500" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Click to view full size
                  </p>
                </div>
              </div>
            </div>

            {/* Verification Checklist (only for pending) */}
            {selectedKyc.status === "pending" && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">
                  Verification Checklist
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="check1"
                      className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-900"
                    />
                    <label
                      htmlFor="check1"
                      className="ml-2 text-sm text-gray-400"
                    >
                      Document is valid and not expired
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="check2"
                      className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-900"
                    />
                    <label
                      htmlFor="check2"
                      className="ml-2 text-sm text-gray-400"
                    >
                      All text is clear and readable
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="check3"
                      className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-900"
                    />
                    <label
                      htmlFor="check3"
                      className="ml-2 text-sm text-gray-400"
                    >
                      Selfie matches document photo
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="check4"
                      className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-900"
                    />
                    <label
                      htmlFor="check4"
                      className="ml-2 text-sm text-gray-400"
                    >
                      Document corners are not cut off
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="check5"
                      className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-900"
                    />
                    <label
                      htmlFor="check5"
                      className="ml-2 text-sm text-gray-400"
                    >
                      No signs of tampering or editing
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
              <Button variant="tertiary" onClick={() => setShowDetails(false)}>
                Close
              </Button>
              {selectedKyc.status === "pending" && (
                <>
                  <Button
                    variant="success"
                    onClick={() => {
                      setShowActionModal({
                        open: true,
                        type: "approve",
                        kyc: selectedKyc,
                      });
                      setShowDetails(false);
                    }}
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setShowActionModal({
                        open: true,
                        type: "reject",
                        kyc: selectedKyc,
                      });
                      setShowDetails(false);
                    }}
                  >
                    <XCircleIcon className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal.open}
        onClose={() =>
          setShowActionModal({ open: false, type: "approve", kyc: null })
        }
        title={
          showActionModal.type === "approve" ? "Approve KYC" : "Reject KYC"
        }
        size="md"
      >
        {showActionModal.kyc && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary-900 flex items-center justify-center mr-3">
                  <UserIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {showActionModal.kyc.user?.username || "N/A"}
                  </p>
                  <p className={globalStyle.caption + " text-gray-400"}>
                    {getDocumentTypeName(showActionModal.kyc.document_type)}
                  </p>
                </div>
              </div>
            </div>

            {showActionModal.type === "approve" ? (
              <>
                <div className={globalStyle.formGroup}>
                  <label className={globalStyle.label}>
                    Approval Notes (Optional)
                  </label>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    className={`${globalStyle.input} min-h-[100px] resize-none`}
                    placeholder="Add any notes or comments..."
                  />
                  <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                    These notes are for internal use only and will not be sent
                    to the user.
                  </p>
                </div>

                <div className="bg-green-900 border border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-300 mb-2">
                    Approving KYC
                  </h4>
                  <ul className="text-sm text-green-200 space-y-1">
                    <li>• User will receive an approval email</li>
                    <li>• User's KYC status will be updated to "approved"</li>
                    <li>• User will gain access to all platform features</li>
                    <li>• This action can be reversed if needed</li>
                  </ul>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="tertiary"
                    onClick={() =>
                      setShowActionModal({
                        open: false,
                        type: "approve",
                        kyc: null,
                      })
                    }
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApproveKyc}
                    loading={actionLoading}
                    fullWidth
                  >
                    Approve KYC
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className={globalStyle.formGroup}>
                  <label className={globalStyle.label}>
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className={`${globalStyle.input} min-h-[100px] resize-none`}
                    placeholder="Explain why this KYC application is being rejected..."
                    required
                  />
                  <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                    Minimum 10 characters. This reason will be sent to the user.
                  </p>
                </div>

                <div className="bg-red-900 border border-red-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-300 mb-2">
                    Common Rejection Reasons
                  </h4>
                  <ul className="text-sm text-red-200 space-y-1">
                    <li>• Blurry or unreadable images</li>
                    <li>• Expired document</li>
                    <li>• Mismatched information</li>
                    <li>• Suspected tampering</li>
                    <li>• Document corners cut off</li>
                  </ul>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="tertiary"
                    onClick={() =>
                      setShowActionModal({
                        open: false,
                        type: "reject",
                        kyc: null,
                      })
                    }
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleRejectKyc}
                    loading={actionLoading}
                    disabled={rejectionReason.trim().length < 10}
                    fullWidth
                  >
                    Reject KYC
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Image Modal */}
      <Modal
        isOpen={imageModal.open}
        onClose={() =>
          setImageModal({ open: false, type: "front", src: "", title: "" })
        }
        title={imageModal.title}
        size="xl"
      >
        <div className="text-center">
          {imageModal.src ? (
            <div className="relative">
              <div className="max-h-[70vh] overflow-auto bg-gray-800 rounded-lg">
                <img
                  src={imageModal.src}
                  alt={imageModal.title}
                  className="max-w-full h-auto mx-auto"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="py-12">
                          <ExclamationTriangleIcon class="w-16 h-16 text-gray-500 mx-auto mb-4" />
                          <p class="text-gray-400">Failed to load image</p>
                          <p class="text-sm text-gray-500 mt-2">The image may have been deleted or is inaccessible</p>
                        </div>
                      `;
                    }
                  }}
                />
              </div>
              <div className="mt-4 flex justify-center space-x-3">
                <Button
                  onClick={() => {
                    window.open(imageModal.src, "_blank");
                  }}
                >
                  Open Full Size
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => {
                    navigator.clipboard.writeText(imageModal.src);
                    alert("Image URL copied to clipboard");
                  }}
                >
                  Copy URL
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-12">
              <ExclamationTriangleIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className={globalStyle.body2 + " text-gray-400"}>
                Image not available
              </p>
              <p className="text-sm text-gray-500 mt-2">
                The image may have been deleted or is inaccessible
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Filters Drawer */}
      <Drawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter KYC Applications"
        size="sm"
      >
        <div className="space-y-6">
          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>Document Type</h4>
            <div className="space-y-2">
              {(["all", "id_card", "passport", "driver_license"] as const).map(
                (type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="radio"
                      name="documentType"
                      className="w-4 h-4 text-primary border-gray-600 focus:ring-primary bg-gray-800"
                    />
                    <span className="ml-2 capitalize text-white">
                      {type === "all" ? "All Types" : getDocumentTypeName(type)}
                    </span>
                  </label>
                ),
              )}
            </div>
          </div>

          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>Submission Date</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">From</label>
                <input type="date" className={globalStyle.input} />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">To</label>
                <input type="date" className={globalStyle.input} />
              </div>
            </div>
          </div>

          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>Sort By</h4>
            <select className={globalStyle.input}>
              <option>Newest First</option>
              <option>Oldest First</option>
              <option>Username A-Z</option>
              <option>Username Z-A</option>
            </select>
          </div>

          <Button
            onClick={() => {
              setShowFilters(false);
            }}
            fullWidth
          >
            Apply Filters
          </Button>

          <Button
            variant="tertiary"
            onClick={() => {
              setStatusFilter("all");
              setSearchTerm("");
              dispatch(setCurrentPage(1));
              setShowFilters(false);
              loadKycDocuments();
            }}
            fullWidth
          >
            Clear All Filters
          </Button>
        </div>
      </Drawer>
    </div>
  );
};

export default AdminKyc;
